<template>
  <div :class="[$style['video-player'],{ [$style['video-player--show']]: show} ]">
    <video
      ref="video"
      playsinline
      disablePictureInPicture
      crossorigin
      :controls="controls"
      preload="auto"
      :class="$style['video-player__video']"
      :loop="loop"
      :muted="muted"
      @click="play" />
  </div>
</template>
<script setup>
import Hls, { Events } from 'hls.js'
const props = defineProps({
  src: {
    type: String,
    default: null
  },
  muted: {
    type: Boolean,
    default: true
  },
  autoplay: {
    type: Boolean,
    default: true
  },
  controls: {
    type: Boolean,
    default: false
  },
  loop: {
    type: Boolean,
    default: true
  },
  forcePlay: {
    type: Boolean,
    default: false
  }
})

const observer = ref(null)
const video = ref(null)
const isPlaying = ref(false)
const show = ref(false)
let timeupdateCallback = null

const play = () => {
  if (!video.value) { return false }
  return video.value.play().catch(() => {})
}

const pause = () => {
  if (!video.value) { return false }
  video.value.pause()
}

onMounted(() => {
  const loadVideo = async () => {
    let previousFrag = null
    try {
      const hls = new Hls({
        capLevelToPlayerSize: true,
        maxBufferSize: 1 * 1000 * 1000,
        maxBufferLength: 5,
        lowLatencyMode: true
      })

      // Events.FRAG_CHANGED is fired when a fragment starts playing
      hls.on(Events.FRAG_CHANGED, (event, data) => {
        // When current framgent quality level is greater then the previous one, then it
        // means that the previous is loaded on a lower level.
        // So we flush the buffer of the video from start to the end of the previous fragment.
        if (data.frag.level > previousFrag?.level) {
          hls.trigger(Events.BUFFER_FLUSHING, {
            startOffset: previousFrag.start,
            endOffset: previousFrag.end - 0.1
          })
        }

        previousFrag = data.frag
      })

      if (video.value.canPlayType('application/vnd.apple.mpegurl')) {
        video.value.src = props.src
        video.value.load()
      } else if (Hls.isSupported()) {
        await hls.loadSource(props.src)
        await hls.attachMedia(video.value)
      } else {
        console.log('not supported')
      }
    } catch (e) {
      console.log('error hier', e)
    }
  }

  // Playstate
  video.value.addEventListener('play', () => {
    isPlaying.value = true
  })
  video.value.addEventListener('pause', () => {
    isPlaying.value = false

    if (props.forcePlay) {
      play()
    }
  })

  // Needed own loop function for Safari. Thanks Safari.
  timeupdateCallback = () => {
    if (!show.value && video.value && video.value.currentTime > 0) {
      show.value = true
    }
    isPlaying.value = true
    if (props.loop && video.value) {
      if (video.value.duration - video.value.currentTime < 0.1) {
        video.value.currentTime = 0
        if (!isPlaying) {
          play()
        }
      }
    }
  }
  video.value.addEventListener('timeupdate', timeupdateCallback)

  const onIntersect = async entries => {
    if (!props.autoplay) { return }
    if (entries[0].isIntersecting) {
      if (!video.value?.src) {
        await loadVideo()
      }
      if (!isPlaying.value) {
        await play()
      }
      return true
    }
    await pause()
  }

  observer.value = new IntersectionObserver(onIntersect, { threshold: 0.1 })
  observer.value.observe(video.value)
})

onActivated(async () => {
  if (!props.autoplay) { return false }
  await play()
})

onDeactivated(() => {
  isPlaying.value = false
  pause()
})

onUnmounted(() => {
  if (video.value) {
    pause()
    video.value.removeEventListener('timeupdate', timeupdateCallback)
  }
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})

</script>
<style lang="scss" module>
.video-player {

  position: relative;
  display: block;
  width: 100%;
  height: 100%;

  &__video {
    object-fit: cover;
    display: block;
    width: 100.5%; // Compensate for browser not rendering half-pixels
    height: 100.5%; // Compensate for browser not rendering half-pixels
    position: relative;
    opacity: 0;
    z-index: 1;
    transition: opacity var(--transition-time-slower) var(--transition-ease-in);
  }

  &__button-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      inset: 0;
      z-index: 2;
  }

  &__progress {
    position: absolute;
    z-index: 20;
    top: calc(var(--spacing ) * 2);
    left: calc(var(--spacing ) * 2);
    width: calc(var(--spacing ) * 4);
    box-shadow: 0 1px 2px var(--black-10-opacity);
  }

  &--show .video-player__video{
    opacity: 1;
  }
}
</style>
